import { action, computed, observable, runInAction } from "mobx";
import { ApiResult, FieldType, ViewModelBase } from "@shoothill/core";
import { StoresInstance } from "../../Stores";

// App
import { AppUrls } from "AppUrls";
import { ApplicantUserAndRelatedDTO } from "Globals/Models/DTO/ApplicantUserAndRelatedDTO";
import { ResendRequestDTO } from "Globals/Models/DTO/ResendRequestDTO";
import { ApplicantUser } from "Globals/Models/DTO/ApplicantUser";
import { ApplicantStatus } from "Globals/Models/ApplicantStatus";
import { ApplicantUserModel } from "Globals/Models/Domain/Applicant/ApplicantUserModel";
import { RegistrationTypeEnum } from "Globals/Models/RegistrationTypeEnum";
import { ApplicantStatusEnum } from "Globals/Models/ApplicantStatusEnum";
import { ApplicationFormStatus, ApplicationFormStatusEnum } from "Globals/Models";
const domainStores = StoresInstance.Domain;

interface UpdateApplicantStatusDTO {
    applicantId: string;
    applicantStatus: ApplicantStatusEnum;
}
interface UpdateCanStartNewApplicationFormDTO {
    applicantId: string;
    canStartNewApplicationForm: boolean;
}

interface InsertAllPayDTO {
    formId: string;
    applicantId: string;
    payment: number;
}

export default class AdminApplicantViewModel extends ViewModelBase<ApplicantUserModel> {
    @observable public applicantStatuses: ApplicantStatus[] = [];

    constructor(id: string | undefined) {
        super(new ApplicantUserModel());
        this.loadApplicant(id);
    }

    @action
    private async loadApplicant(id: string | undefined): Promise<void> {
        if (!id) return;
        this.setIsLoading(true);
        const apiResult = await this.Get<ApplicantUserAndRelatedDTO>(AppUrls.Server.Admin.GetApplicantUser + "/?id=" + id);
        if (!apiResult.wasSuccessful) return;
        this.model.reset();
        this.model.fromDto(apiResult.payload.applicantUser);
        runInAction(() => {
            this.applicantStatuses = apiResult.payload.applicantStatuses;
        });

        this.setIsLoading(false);
    }

    public refresh() {
        if (this.model.id) {
            this.loadApplicant(this.model.id);
        }
    }

    @action
    public async handleResendRegistration(type: RegistrationTypeEnum): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let resendRequest: ResendRequestDTO = {
            applicantId: this.model.id,
            registrationType: type,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.ResendRegistration, resendRequest);
        if (apiResult.wasSuccessful) {
            domainStores.OpenSnackBar("Registration link sent", "success");
        } else {
            domainStores.OpenSnackBar(`Error: ${apiResult.errors[0].message}`, "error");
        }
        this.setIsLoading(false);
    }
    @action
    public async updateApplicatStatus(applicantStatus: ApplicantStatusEnum): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let payload: UpdateApplicantStatusDTO = {
            applicantId: this.model.id,
            applicantStatus: applicantStatus,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.UpdateApplicantStatus, payload);
        if (apiResult.wasSuccessful) {
            this.refresh();
            domainStores.OpenSnackBar("Done", "success");
        } else {
            domainStores.OpenSnackBar(`Error`, "error");
        }

        this.setIsLoading(false);
    }

    @action
    public async insertAllPay(formId: string, payment: number): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let payload: InsertAllPayDTO = {
            formId: formId,
            applicantId: this.model.id,
            payment: payment,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Applicant.InsertAllPay, payload);
        if (apiResult.wasSuccessful) {
            domainStores.OpenSnackBar("Done", "success");
        } else {
            domainStores.OpenSnackBar(`Error`, "error");
        }

        this.setIsLoading(false);
    }

    @action
    public async updateCanStartNewApplicationForm(value: boolean = true): Promise<void> {
        this.setIsLoading(true);
        if (this.model.id == null) return;
        let payload: UpdateCanStartNewApplicationFormDTO = {
            applicantId: this.model.id,
            canStartNewApplicationForm: value,
        };
        const apiResult = await this.Post<boolean>(AppUrls.Server.Admin.UpdateCanStartNewApplicationForm, payload);
        if (apiResult.wasSuccessful) {
            this.refresh();
            domainStores.OpenSnackBar("Done", "success");
        } else {
            domainStores.OpenSnackBar(`Error`, "error");
        }
        this.setIsLoading(false);
    }

    @computed
    public get getFullName(): string {
        return this.model.firstName + " " + this.model.surname;
    }

    @action
    public handleFieldChange = (fieldName: keyof FieldType<ApplicantUserModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    public async isFieldValid(fieldName: keyof FieldType<ApplicantUserModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
